import { motion } from "framer-motion";
import {
  type FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import type { BlackboardInstance } from "./blackboardModels";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useOrgMcpServers } from "../../hooks/useOrgMcpServers";
import { isAdmin } from "../../utils/rbac";
import { Button } from "../common/Button";
import { LoadingSpinner, LoadingState } from "../common/LoadingState";
import { ActorLevelPip } from "./ActorLevelPip";
import { ActorPreviewCard } from "./ActorPreviewCard";
import { getAvatarBackgroundColor, InstanceAvatar } from "./InstanceAvatar";
import { McpServerChip } from "./McpServerChip";
import { ModelChip } from "./ModelChip";
import { ReasoningEffortChip } from "./ReasoningEffortChip";
import { TemplateDialog } from "./TemplateDialog";
import { useBlackboard } from "./useBlackboard";
import {
  type ActorTemplate,
  type CreateTemplateInput,
  useTemplates,
} from "./useTemplates";

const LineageChildAvatar: FC<{
  instance: BlackboardInstance;
  setRef: (el: HTMLDivElement | null) => void;
}> = ({ instance, setRef }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number } | null>(
    null,
  );

  useEffect(() => {
    if (!previewPos) {
      return;
    }
    // Re-measure on scroll / resize so the portal stays anchored to the avatar.
    const update = () => {
      const el = wrapperRef.current;
      if (!el) {
        return;
      }
      const r = el.getBoundingClientRect();
      setPreviewPos({ x: r.left + r.width / 2, y: r.top });
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [previewPos]);

  const onEnter = () => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const r = el.getBoundingClientRect();
    setPreviewPos({ x: r.left + r.width / 2, y: r.top });
  };
  const onLeave = () => setPreviewPos(null);

  const navigate = useNavigate();
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      void navigate(`/agents-instances?highlight=${instance.id}`);
    },
    [navigate, instance.id],
  );

  return (
    <div
      ref={(el) => {
        wrapperRef.current = el;
        setRef(el);
      }}
      className="relative inline-block h-6 w-6 flex-shrink-0 cursor-pointer ring-1 ring-white dark:ring-slate-900"
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <InstanceAvatar
        seed={instance.avatarSeed}
        size="sm"
        status={instance.status}
        animated
      />
      <ActorLevelPip level={instance.evolution} />
      {previewPos &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-full pb-2"
            style={{ left: previewPos.x, top: previewPos.y }}
          >
            <ActorPreviewCard instance={instance} />
          </div>,
          document.body,
        )}
    </div>
  );
};

export const TemplatesPage: FC = () => {
  const mcpAuth = useDashboardAuth();
  const { servers: mcpServers } = useOrgMcpServers(mcpAuth.organizationId);
  const mcpServerNames = useMemo(
    () => new Map(mcpServers.map((server) => [server.id, server.display_name])),
    [mcpServers],
  );
  const {
    createTemplate,
    deleteTemplate,
    error,
    loading,
    templates,
    updateTemplate,
  } = useTemplates();
  // Sibling-page data for lineage lookup. Re-uses the WS subscription via the
  // Shared realtime context — no additional network cost beyond the REST fetch.
  const { instances } = useBlackboard();
  const auth = useDashboardAuth();
  const canAdmin = isAdmin(auth.orgRole, auth.globalRole);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ActorTemplate | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const instancesByTemplate = useMemo(() => {
    const map = new Map<string, BlackboardInstance[]>();
    for (const inst of instances) {
      if (!inst.templateId || inst.status === "killed") {
        continue;
      }
      const bucket = map.get(inst.templateId);
      if (bucket) {
        bucket.push(inst);
      } else {
        map.set(inst.templateId, [inst]);
      }
    }
    return map;
  }, [instances]);

  // Rank templates by "polish" — how much the org has invested in them.
  // Like a character-select screen: most cared-for entries float to the top.
  //
  //   Source: overridden (customized seed) > seed (canon) > user (created)
  //   Identity prompt defined         → +1
  //   IsSingleton                     → +2 (declared singular = important)
  //   Has description                 → +1
  //   Running instance count          → +1 each (capped at +3)
  //
  // Ties break on the source tier, then on updatedAt (recently-touched first).
  const rankedTemplates = useMemo(() => {
    const sourceRank: Record<string, number> = {
      overridden: 3,
      seed: 2,
      user: 1,
    };
    const polish = (t: ActorTemplate, running: number) =>
      (t.identityPrompt ? 1 : 0) +
      (t.isSingleton ? 2 : 0) +
      (t.description ? 1 : 0) +
      Math.min(running, 3);
    return [...templates].toSorted((a, b) => {
      const ra = sourceRank[a.source] ?? 0;
      const rb = sourceRank[b.source] ?? 0;
      if (ra !== rb) {
        return rb - ra;
      }
      const pa = polish(a, (instancesByTemplate.get(a.id) ?? []).length);
      const pb = polish(b, (instancesByTemplate.get(b.id) ?? []).length);
      if (pa !== pb) {
        return pb - pa;
      }
      return (
        Date.parse(b.updatedAt || b.createdAt) -
        Date.parse(a.updatedAt || a.createdAt)
      );
    });
  }, [templates, instancesByTemplate]);

  const handleCreate = useCallback(
    async (input: CreateTemplateInput) => {
      setSaving(true);
      try {
        await createTemplate(input);
        setShowCreateDialog(false);
      } finally {
        setSaving(false);
      }
    },
    [createTemplate],
  );

  const handleUpdate = useCallback(
    async (input: CreateTemplateInput) => {
      if (!editingTemplate) {
        return;
      }
      setSaving(true);
      try {
        // Forward everything the dialog produced, then apply the conversions
        // this endpoint wants. The enumeration this replaces dropped four
        // fields once -- isSingleton, heartbeatIntervalS,
        // consolidationSchedule, compactContextGrowthThreshold -- and then
        // dropped mcpServerIds a second time, because "add a field to the
        // dialog" silently also meant "remember to add it here". Spreading
        // removes that second step instead of documenting it.
        const { modelRef, ...forwarded } = input;
        await updateTemplate(editingTemplate.id, {
          ...forwarded,
          consolidationSchedule: input.consolidationSchedule ?? null,
          description: input.description ?? null,
          heartbeatIntervalS: input.heartbeatIntervalS ?? null,
          identityPrompt: input.identityPrompt ?? null,
          issueWorkspaceDefault: input.issueWorkspaceDefault ?? null,
          reasoningEffort: input.reasoningEffort ?? null,
          tickIntervalMs: input.tickIntervalMs ?? null,
          unarmedParkGraceS: input.unarmedParkGraceS ?? null,
          // Omit modelRef when unchanged: a template holding a model that has
          // left the catalog would otherwise fail validation on EVERY save,
          // blocking edits to unrelated fields until the model is swapped.
          ...((modelRef ?? null) === editingTemplate.modelRef
            ? {}
            : { modelRef: modelRef ?? null }),
        });
        setEditingTemplate(null);
      } finally {
        setSaving(false);
      }
    },
    [editingTemplate, updateTemplate],
  );

  const handleRevertToSeed = useCallback(async () => {
    if (!editingTemplate) {
      return;
    }
    setSaving(true);
    try {
      await deleteTemplate(editingTemplate.id);
      setEditingTemplate(null);
    } finally {
      setSaving(false);
    }
  }, [editingTemplate, deleteTemplate]);

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await deleteTemplate(id);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteTemplate],
  );

  if (loading) {
    return <LoadingState message="Loading templates…" />;
  }

  if (error) {
    return (
      <div className="flex h-40 items-center justify-center">
        <p className="font-mono text-xs text-rose-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
            [ TEMPLATES ]
          </p>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Actor Templates
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Reusable configurations for spawning actors
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreateDialog(true)}>
          + New Template
        </Button>
      </div>

      {/* Empty state */}
      {templates.length === 0 && (
        <div className="flex flex-col items-center gap-4 py-16">
          <div className="flex items-center gap-1.5 font-mono text-2xl font-bold text-slate-600/40 dark:text-slate-600">
            <span>[</span>
            <span className="text-emerald-500/30 dark:text-emerald-500/20">
              —
            </span>
            <span>]</span>
          </div>
          <p className="font-mono text-xs text-slate-400 dark:text-slate-500">
            No templates yet — create one to get started.
          </p>
          <Button size="sm" onClick={() => setShowCreateDialog(true)}>
            Create Template
          </Button>
        </div>
      )}

      {/* Grid */}
      {rankedTemplates.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rankedTemplates.map((t) => (
            <TemplateCard
              mcpServerNames={mcpServerNames}
              key={t.id}
              template={t}
              childInstances={instancesByTemplate.get(t.id) ?? []}
              deleting={deletingId === t.id}
              canAdmin={canAdmin}
              onEdit={() => setEditingTemplate(t)}
              onDelete={() => void handleDelete(t.id)}
            />
          ))}
        </div>
      )}

      {/* Create dialog */}
      {showCreateDialog && (
        <TemplateDialog
          onClose={() => setShowCreateDialog(false)}
          onSave={handleCreate}
          saving={saving}
        />
      )}

      {/* Edit dialog */}
      {editingTemplate && (
        <TemplateDialog
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={handleUpdate}
          // Admins editing an overridden template get a "Reset to seed" button
          // That drops the override row and reverts to the plain seed.
          onRevertToSeed={
            canAdmin && editingTemplate.source === "overridden"
              ? handleRevertToSeed
              : undefined
          }
          saving={saving}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TemplateCard
// ---------------------------------------------------------------------------

interface TemplateCardProps {
  template: ActorTemplate;
  /** Maps a server id to its display name. Resolved once by the page, not per
   *  card: a card that fetched its own copy would fetch it once per template. */
  mcpServerNames: ReadonlyMap<string, string>;
  childInstances: BlackboardInstance[];
  deleting: boolean;
  /** True when the viewer can edit platform (seeded) templates. Non-admins
   *  can still edit/delete their own scope='org' templates regardless. */
  canAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

// Particle animation — three dots flowing from the template avatar to the
// Children avatars along the lineage strip. Activates on hover when there's
// At least one child; `custom` index picks the destination avatar slot.
const MAX_PARTICLES = 3;

const getEditTitle = (isPlatform: boolean, source: ActorTemplate["source"]) => {
  if (!isPlatform) {
    return "Edit template";
  }
  if (source === "overridden") {
    return "Edit override (seed preserved)";
  }
  return "Edit — creates an override for this org";
};

const TemplateNameBlock: FC<{
  accentColor: string;
  stars: number;
  template: ActorTemplate;
}> = ({ accentColor, stars, template }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1.5">
      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
        {template.name}
      </p>
      {stars > 0 && (
        <span
          className="font-mono text-[10px] leading-none tracking-tighter"
          style={{ color: accentColor }}
          title={`Polish rank ${stars}/3 — identity, singularity, description, and live instances all add to this score`}
          aria-label={`rank ${stars} of 3`}
        >
          {"★".repeat(stars)}
          <span className="opacity-30">{"★".repeat(3 - stars)}</span>
        </span>
      )}
    </div>
    {template.description && (
      <p className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
        {template.description}
      </p>
    )}
  </div>
);

function buildParticleTransition(i: number): {
  delay: number;
  duration: number;
  ease: "linear";
  repeat: number;
  repeatDelay: number;
  times: number[];
} {
  return {
    delay: i * 0.35,
    duration: 1.8,
    ease: "linear",
    repeat: Infinity,
    repeatDelay: 0.3,
    times: [0, 0.5, 0.92, 1],
  };
}

const TemplateCard: FC<TemplateCardProps> = ({
  template,
  mcpServerNames,
  childInstances,
  deleting,
  canAdmin,
  onEdit,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const hasChildren = childInstances.length > 0;
  const accentColor = useMemo(
    () => getAvatarBackgroundColor(template.avatarSeed),
    [template.avatarSeed],
  );
  // Platform rows (seed or overridden) are admin-gated for edit/delete.
  // Scope='org' rows remain fully editable by any org member.
  const isPlatform = template.scope === "platform";
  const canEdit = !isPlatform || canAdmin;
  const canDelete =
    !isPlatform || (canAdmin && template.source === "overridden");
  // Prestige: 0–3 stars next to the name based on the same polish score
  // Used for sorting. Makes the "invest-in-your-roster" pattern legible at
  // A glance, like a character-select screen.
  const polishScore =
    (template.identityPrompt ? 1 : 0) +
    (template.isSingleton ? 2 : 0) +
    (template.description ? 1 : 0) +
    Math.min(childInstances.length, 3);
  const stars = (() => {
    if (polishScore >= 6) {
      return 3;
    }
    if (polishScore >= 3) {
      return 2;
    }
    if (polishScore >= 1) {
      return 1;
    }
    return 0;
  })();
  const particleCount = Math.min(childInstances.length, MAX_PARTICLES);

  // Particle flight coordinates are computed from the real DOM — start at the
  // Middle-bottom of the template avatar, land at the centre of each child
  // Avatar in the lineage strip. Measured in px relative to the card root.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const templateAvatarRef = useRef<HTMLDivElement | null>(null);
  const childAvatarRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [trajectories, setTrajectories] = useState<
    { start: { x: number; y: number }; end: { x: number; y: number } }[]
  >([]);

  const measureTrajectories = useCallback(() => {
    if (!hasChildren) {
      setTrajectories([]);
      return;
    }
    const card = cardRef.current?.getBoundingClientRect();
    const tpl = templateAvatarRef.current?.getBoundingClientRect();
    if (!card || !tpl) {
      return;
    }
    const start = {
      x: tpl.left - card.left + tpl.width / 2,
      y: tpl.top - card.top + tpl.height, // Middle-bottom of avatar
    };
    const next = Array.from({ length: particleCount }).flatMap((_, i) => {
      const el = childAvatarRefs.current[i];
      if (!el) {
        return [];
      }
      const r = el.getBoundingClientRect();
      return [
        {
          end: {
            x: r.left - card.left + r.width / 2,
            y: r.top - card.top + r.height / 2,
          },
          start,
        },
      ];
    });
    setTrajectories(next);
  }, [hasChildren, particleCount]);

  useLayoutEffect(() => {
    measureTrajectories();
  }, [measureTrajectories, childInstances.length]);

  // Build per-orb animate target inline — avoid variants + custom so framer
  // Has one prop path to watch per orb. Stripped-down keyframes: just 3
  // Points (start → midpoint with dip → end), equal spacing, linear ease.
  const buildAnimate = (i: number) => {
    const t = trajectories[i];
    if (!t || !isHovered) {
      return { opacity: 0, x: t?.start.x ?? 0, y: t?.start.y ?? 0 };
    }
    const midX = (t.start.x + t.end.x) / 2;
    const midY = (t.start.y + t.end.y) / 2 + 10;
    return {
      opacity: [0, 1, 1, 0],
      x: [t.start.x, midX, t.end.x, t.end.x],
      y: [t.start.y, midY, t.end.y, t.end.y],
    };
  };

  return (
    <motion.div
      ref={cardRef}
      className="group relative flex flex-col gap-0 overflow-hidden border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
      whileHover={{ y: -1 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      onMouseEnter={() => {
        // Re-measure on hover: lineage layout can shift after initial mount
        // (websocket data, font load, grid resize), so always take a fresh
        // Reading right before the animation plays.
        measureTrajectories();
        setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top accent bar — mirrors the avatar's generated background color */}
      <div className="h-0.5 w-full" style={{ backgroundColor: accentColor }} />

      {/* Hover-triggered particle layer — orbs depart the template avatar's
          middle-bottom and land on each child avatar in the lineage strip.
          Positioned as a card-root overlay so coordinates stay valid regardless
          of the avatar-row flex geometry. */}
      {hasChildren && trajectories.length > 0 && (
        <div className="pointer-events-none absolute inset-0 z-20">
          {trajectories.map((_, i) => (
            <motion.span
              key={i}
              animate={buildAnimate(i)}
              transition={buildParticleTransition(i)}
              className="absolute left-0 top-0 block h-1.5 w-1.5 rounded-full"
              style={{
                backgroundColor: accentColor,
                boxShadow: `0 0 6px ${accentColor}a6`,
                marginLeft: -3,
                marginTop: -3,
              }}
            />
          ))}
        </div>
      )}

      <div className="relative flex flex-col gap-3 p-4">
        {/* Avatar + name row */}
        <div className="flex items-start justify-between gap-3">
          <div className="relative flex items-center gap-3">
            <div
              ref={templateAvatarRef}
              className="relative inline-block h-10 w-10 flex-shrink-0"
            >
              <InstanceAvatar seed={template.avatarSeed} status="waiting" />
            </div>

            <TemplateNameBlock
              accentColor={accentColor}
              stars={stars}
              template={template}
            />
          </div>

          {/* Action buttons — admin-gated on platform (seed/overridden) rows.
              Absolute-positioned so they float above the avatar-row flex,
              unaffected by description width / ModelChip / badges. z-30 sits
              above the particle layer (z-20) and the hover-tint overlay. */}
          <div
            className="absolute right-3 top-2 z-30 flex shrink-0 items-center gap-1 transition-opacity duration-150"
            style={{
              opacity: isHovered ? 1 : 0,
              pointerEvents: isHovered ? "auto" : "none",
            }}
          >
            {canEdit ? (
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
                title={getEditTitle(isPlatform, template.source)}
                onClick={onEdit}
              >
                <PencilIcon />
              </button>
            ) : (
              <span
                className="flex h-6 w-6 items-center justify-center text-slate-300 dark:text-slate-600"
                title="Seeded template — only org admins can edit"
              >
                <PencilIcon />
              </span>
            )}
            {canDelete ? (
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center text-slate-400 transition-colors hover:text-rose-500"
                title={
                  isPlatform
                    ? "Revert override — restores seed defaults"
                    : "Delete template"
                }
                disabled={deleting}
                onClick={onDelete}
              >
                {deleting ? <LoadingSpinner size="sm" /> : <TrashIcon />}
              </button>
            ) : null}
          </div>
        </div>

        {/* Meta tags */}
        <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2.5 dark:border-slate-800">
          {template.modelRef ? (
            <ModelChip modelRef={template.modelRef} />
          ) : (
            <span className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              default model
            </span>
          )}
          {template.reasoningEffort && (
            <ReasoningEffortChip
              compact
              reasoningEffort={template.reasoningEffort}
              source="template"
            />
          )}
          {template.tickIntervalMs != null && (
            <span className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              tick {formatMs(template.tickIntervalMs)}
            </span>
          )}
          {template.identityPrompt && (
            <span
              className="border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest"
              style={{
                backgroundColor: `${accentColor}1a`,
                borderColor: `${accentColor}4d`,
                color: accentColor,
              }}
            >
              identity
            </span>
          )}
          {template.source === "seed" && (
            <span
              className="border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-sky-600 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-400"
              title="Seeded platform template — admins edit via override"
            >
              ◆ seeded
            </span>
          )}
          {template.source === "overridden" && (
            <span
              className="border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest"
              style={{
                // Gold — signals "this seeded template has been customized
                // By an admin". Warmer/brighter than amber so it reads as a
                // Distinct class from the amber thermal "warm" indicator
                // And from hazard-oriented amber badges elsewhere.
                backgroundColor: "#d4af3722",
                borderColor: "#d4af3766",
                color: "#e5bc4f",
              }}
              title="Seeded template with an org override applied — delete to revert"
            >
              ◈ overridden
            </span>
          )}
          <McpServerChip
            serverIds={template.mcpServerIds ?? []}
            serverNames={mcpServerNames}
          />
          {template.isSingleton && (
            <span
              className="border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-emerald-600 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
              title="Singleton — at most one live instance per org. Spawn blocks if one already exists."
            >
              ◇ solo
            </span>
          )}
        </div>

        {/* Lineage footer — running instances spawned from this template */}
        <button
          type="button"
          onClick={() => void navigate("/agents-instances")}
          className="flex items-center justify-between border-t border-slate-100 pt-2.5 text-left transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/40"
          disabled={!hasChildren}
        >
          <div className="flex items-center gap-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
              {hasChildren ? "[ live ]" : "[ idle ]"}
            </p>
            {hasChildren ? (
              <div className="flex -space-x-1.5">
                {childInstances.slice(0, 5).map((inst, idx) => (
                  <LineageChildAvatar
                    key={inst.id}
                    instance={inst}
                    setRef={(el) => {
                      childAvatarRefs.current[idx] = el;
                    }}
                  />
                ))}
                {childInstances.length > 5 && (
                  <span className="flex h-6 w-6 items-center justify-center border border-slate-200 bg-slate-100 font-mono text-[9px] tabular-nums text-slate-500 ring-1 ring-white dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-900">
                    +{childInstances.length - 5}
                  </span>
                )}
              </div>
            ) : (
              <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
                no running instances
              </span>
            )}
          </div>
          {hasChildren && (
            <span className="font-mono text-[10px] tabular-nums text-slate-400 transition-colors group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300">
              {childInstances.length} →
            </span>
          )}
        </button>
      </div>

      {/* Hover tint overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 dark:bg-white/[0.02]" />
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMs(ms: number): string {
  if (ms >= 60_000) {
    const m = Math.floor(ms / 60_000);
    const s = Math.round((ms % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${Math.round(ms / 1000)}s`;
}

// Micro icon components (inline SVGs — no extra dep)
function PencilIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 9h8l1-9" />
    </svg>
  );
}
